import { Alert, Box, IconButton, InputAdornment } from '@mui/material';
import { VisibilityOff, Visibility, Info as InfoIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { useEffect, useState } from 'react';
import { generateVerificationToken } from '../../features/user/utils';
import { clearPasswordError, handleSignUpSubmit, updateSignUpFormField } from '../../features/user/userSlice';
import PasswordVerification from './PasswordVerification';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { Typography } from '../new/Typography';
import { Button } from '../new/Button';
import { TextField } from '../new/inputs/TextField';

const inputWrapperStyles = {
  display: 'flex',
  width: '100%',
  gap: '0.625rem',
};

type InvitedUserToken = {
  memeberEmail: string;
  iat: string;
  memeberId: number;
  organizationId: number;
  firstName?: string;
  lastName?: string;
};

const RegistrationForm = () => {
  const dispatch: AppDispatch = useDispatch();

  const formData = useSelector((state: RootState) => state.user.signUpForm);

  const errors = useSelector((state: RootState) => state.user.signUpFormErrors);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [invToken, setInvToken] = useState<string | undefined>(undefined);

  const location = useLocation();

  const navigate = useNavigate();

  const navigateToSignIn = () => {
    navigate('/sign-in');
  };

  useEffect(() => {
    const hasErrors = Object.values(errors).some((error) => error !== '' && error !== null);

    if (!hasErrors && isSubmitted) {
      dispatch(generateVerificationToken(invToken));
    } else {
      setIsSubmitted(false);
    }
  }, [errors, dispatch, isSubmitted, invToken]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const inviteToken = queryParams.get('token');

    if (inviteToken) {
      const decodedInvToken: InvitedUserToken = jwtDecode(inviteToken);
      setInvToken(inviteToken);

      dispatch(
        updateSignUpFormField({
          fieldName: 'email',
          value: decodedInvToken?.memeberEmail,
        }),
      );

      if (decodedInvToken?.firstName) {
        dispatch(
          updateSignUpFormField({
            fieldName: 'firstName',
            value: decodedInvToken?.firstName,
          }),
        );
      }

      if (decodedInvToken?.lastName) {
        dispatch(
          updateSignUpFormField({
            fieldName: 'lastName',
            value: decodedInvToken?.lastName,
          }),
        );
      }

      dispatch(
        updateSignUpFormField({
          fieldName: 'isInvOrg',
          value: true,
        }),
      );
    }
  }, [location.search]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    dispatch(handleSignUpSubmit());

    setIsSubmitted(true);
  };
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleChange = (e: any) => {
    e.preventDefault();
  };

  const handlePasswordChange = (e: any) => {
    dispatch(
      updateSignUpFormField({
        fieldName: 'password',
        value: e.target.value,
      }),
    );
    if (errors.passwordErrorType) {
      dispatch(clearPasswordError());
    }
  };

  return (
    <Box component="form" noValidate sx={{ width: '50%', mt: 2 }}>
      <div style={inputWrapperStyles}>
        <div style={{ width: '100%' }}>
          <TextField
            error={errors.firstNameError}
            placeholder="Add..."
            inputProps={{ maxLength: 20 }}
            id="firstName"
            name="firstName"
            label="First Name"
            required
            autoComplete="given-name"
            value={formData.firstName}
            onChange={(e) =>
              dispatch(
                updateSignUpFormField({
                  fieldName: 'firstName',
                  value: e.target.value,
                }),
              )
            }
          />
        </div>
        <div style={{ width: '100%' }}>
          <TextField
            error={errors.lastNameError}
            placeholder="Add..."
            inputProps={{ maxLength: 20 }}
            id="lastName"
            name="lastName"
            value={formData.lastName}
            required
            label="Last Name"
            onChange={(e) =>
              dispatch(
                updateSignUpFormField({
                  fieldName: 'lastName',
                  value: e.target.value,
                }),
              )
            }
          />
        </div>
      </div>
      <TextField
        error={errors.emailError}
        placeholder="Add..."
        id="email"
        name="email"
        label="Email Address"
        required
        value={formData.email}
        onChange={(e) =>
          dispatch(
            updateSignUpFormField({
              fieldName: 'email',
              value: e.target.value,
            }),
          )
        }
      />
      <TextField
        label="Password"
        required
        value={formData.password}
        name="password"
        id="password"
        removeSpacer
        containerSX={{ mb: 1 }}
        onCut={handleChange}
        onCopy={handleChange}
        onPaste={handleChange}
        type={showPassword ? 'text' : 'password'}
        endAdornment={
          <InputAdornment position="end" sx={{ mr: 2 }}>
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
        onChange={handlePasswordChange}
        error={errors.passwordError}
      />
      <PasswordVerification errors={errors.passwordErrorType} password={formData.password} />
      <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 400, mb: 1, mt: 2 }}>
        By continuing, you agree to the{' '}
        <Link target="_blank" rel="noreferrer" to="https://www.certificateofservice.com/terms-of-service">
          Terms Of Service
        </Link>{' '}
        and{' '}
        <Link target="_blank" rel="noreferrer" to="https://www.certificateofservice.com/privacy-policy">
          Privacy Policy
        </Link>
        .
      </Typography>
      <Button type="primary" sx={{ mt: 1 }} onClick={handleSubmit}>
        Get the Code
      </Button>
      {errors.requestError && (
        <Alert sx={{ display: 'flex', alignItems: 'center' }} severity="error">
          {errors.requestError}
        </Alert>
      )}
      <Box sx={{ textAlign: 'center', marginTop: (theme) => theme.spacing(2) }}>
        <Button
          type="outline"
          sx={{
            background: '#fff',
            color: '#000',
            borderRadius: '0rem',
            borderColor: '#d6d6d6',
            textTransform: 'initial',
            mb: 8,
          }}
          onClick={navigateToSignIn}
        >
          Already have an account? Sign In!
        </Button>
      </Box>
    </Box>
  );
};
export default RegistrationForm;
